import { useState, useEffect, use } from "react";
import { TextField } from "../common/inputFiled";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { LocationModal } from "./locationModel";

export const AddressPage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [searchInputs, setSearchInputs] = useState({ name: "", pincode: "" });
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isExisting, setIsExisting] = useState(false); // ✅ Track existing record
  const token = Cookies.get("auth_token");

  const [formData, setFormData] = useState({
    home: {
      flat_number: "",
      building_name: "",
      street_name: "",
      city_region_country: "",
      pin_code: "",
      google_map_link: "",
    },
    work: {
      flat_number: "",
      building_name: "",
      street_name: "",
      city_region_country: "",
      pin_code: "",
      google_map_link: "",
    },
    work_phone: "",
  });


  // 🔹 Fetch address data on mount
  useEffect(() => {
    fetchAddressData();
  }, []);

  const fetchAddressData = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/get_address`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();

      if (data.status === "1" && data.data?.[0]?.address) {
        const addr = data.data[0].address;
        setFormData({
          home: {
            flat_number: addr?.home?.flat_number || "",
            building_name: addr?.home?.building_name || "",
            street_name: addr?.home?.street_name || "",
            city_region_country: addr?.home?.city_region_country || "",
            pin_code: addr?.home?.pin_code || "",
            google_map_link: addr?.home?.google_map_link || "",
          },
          work: {
            flat_number: addr?.work?.flat_number || "",
            building_name: addr?.work?.building_name || "",
            street_name: addr?.work?.street_name || "",
            city_region_country: addr?.work?.city_region_country || "",
            pin_code: addr?.work?.pin_code || "",
            google_map_link: addr?.work?.google_map_link || "",
          },
          work_phone: addr?.work_phone || "",
        });

        setIsExisting(true); // ✅ Mark as record exists
      } else {
        setIsExisting(false); // ✅ No record found
        toast.dismiss();
        toast.error("No address found. Please add new address.");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    }
  };

  // 🔹 Handle input change
  const handleChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value || "" },
    }));
  };

  // 🔹 Submit updated/inserted address
  const handleSubmit = async () => {
    try {
      const payload = new URLSearchParams({
        home_flat_number: formData?.home?.flat_number || "",
        home_building_name: formData?.home?.building_name || "",
        home_street_name: formData?.home?.street_name || "",
        home_city_region_country: formData?.home?.city_region_country || "",
        home_pin_code: formData?.home?.pin_code || "",
        home_google_map_link: formData?.home?.google_map_link || "",
        work_flat_number: formData?.work?.flat_number || "",
        work_building_name: formData?.work?.building_name || "",
        work_street_name: formData?.work?.street_name || "",
        work_city_region_country: formData?.work?.city_region_country || "",
        work_pin_code: formData?.work?.pin_code || "",
        work_google_map_link: formData?.work?.google_map_link || "",
        work_phone: formData?.work_phone || "",
      });

      // ✅ Choose correct endpoint
      const endpoint = isExisting
        ? "/update_address"
        : "/insert_address";

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
          body: payload.toString(),
        }
      );

      const data = await res.json();

      if (data.status === "1") {
        toast.success(
          isExisting
            ? "Address updated successfully!"
            : "Address added successfully!"
        );
        setIsEditing(false);
        setIsExisting(true); // ✅ Mark as existing after insert
        fetchAddressData(); // refresh data
      } else {
        toast.dismiss();
        toast.error(data.message || "Failed to save address");
      }
    } catch (err) {
      toast.dismiss();

      toast.error(err.message);
    }
  };

  // 🔹 On selecting a location
  const handleSelectLocation = (item) => {
    setFormData((prev) => ({
      ...prev,
      work: {
        flat_number: item?.flat_number || "",
        building_name: item?.building_name || "",
        street_name: item?.street_name || "",
        city_region_country: item?.work_address || "",
        pin_code: item?.pin_code || "",
        google_map_link: item?.google_map_link || "",
      },
    }));
    setSelectedLocation(item);
    setShowLocationModal(false);
    toast.success("Work address updated from location");
  };

  return (
    <div>
      <div className="rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Address</h2>

          {!isEditing ? (
            <button
              className="mr-4"
              onClick={() => {
                setShowLocationModal(true);
                setIsEditing(true);
              }}
              type="button"
            >
              <img src="./edit.svg" alt="Edit" />
            </button>
          ) : (
            <button
              className="mr-4"
              onClick={() => {
                setShowLocationModal(true);
                setSearchResults([]);
                setSearchInputs({ name: "", pincode: "" });
                setSelectedLocation(null);
              }}
              type="button"
            >
              <img src="./location.svg" alt="Location" />
            </button>
          )}
        </div>

        {/* 🔹 Address Fields */}
        <div className="h-[86dvh] overflow-y-auto [&::-webkit-scrollbar]:hidden">
          <div className="mt-4 grid gap-4">
            {["home", "work"].map((type) => (
              <div key={type}>
                <div className="py-1 px-2 mb-3 rounded-2xl text-[16px] font-semibold text-black bg-[#F7F8F8] w-max border-2 shadow-[0_4px_4px_rgba(0,0,0,0.25)]">
                  {type === "home" ? "Home Address" : "Work Address"}
                </div>
                {[
                  ["flat_number", "Flat Number"],
                  ["building_name", "Building Name"],
                  ["street_name", "Street Name"],
                  ["city_region_country", "City, Region, Country"],
                  ["pin_code", "Pin Code"],
                  ["google_map_link", "Google Map Link"],
                ].map(([field, label]) => (
                  <TextField
                    key={field}
                    label={`${label}`}
                    placeholder={`Enter ${label}`}
                    value={formData?.[type]?.[field] || ""}
                    onChange={(e) =>
                      handleChange(type, field, e.target.value)
                    }
                    disabled={!isEditing}
                  />
                ))}
              </div>
            ))}

            <TextField
              label="Work Phone"
              placeholder="Enter Work Phone"
              value={formData?.work_phone || ""}
              onChange={(e) =>
                setFormData({ ...formData, work_phone: e.target.value || "" })
              }
              disabled={!isEditing}
            />
          </div>

          {isEditing && (
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                className="mb-2 submit-button"
              >
                {isExisting ? "Update" : "Submit"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 🔹 Location Modal */}
      <LocationModal
        show={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onSelect={(item) => handleSelectLocation(item)}
      />
    </div>
  );
};
